import React, { useState, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import styles from "./Forms.module.css";
import Submission from "../UILibrary/Submission";
import {
  IReplyInquiryFormData,
  IReplyInquiryFormProps,
} from "@model/contactus.model";
import { defaultInquirieValues } from "Utils/Global/globalConstants";
import { ContactService } from "../../../Logic/ContactService";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { useForceLogoutAndRedirect } from "../../../Logic/Functions/Hooks/useForceLogoutAndRedirect";
import { UnauthorizedPopupStoreImplementation } from "Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation";
import { useNavigate } from "react-router-dom";

const MIN_REPLY_LENGTH = 50;

const ReplyToInquiryForm: React.FC<IReplyInquiryFormProps> = ({
  originalMessage,
  title,
  onClose,
  contactId,
  onReplySuccess,
}) => {
  const replySubject = useMemo(
    () => ContactService.ensureRePrefix(title ?? ""),
    [title]
  );

  const forceLogoutAndRedirect =useForceLogoutAndRedirect();

  const { control, handleSubmit, reset } = useForm<IReplyInquiryFormData>({
    mode: "onSubmit",
    defaultValues: defaultInquirieValues,
  });
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const contactService = new ContactService();
  const authStore = UserAuthenticationStoreImplementation();

  const onValidSubmit = async (values: IReplyInquiryFormData) => {
    const payload: IReplyInquiryFormData = {
      ...values,
      title: replySubject,
    };

    const msg = await contactService.handleReplyToInquiry({
      data: payload,
      contactId,
      reset,
      onAuthError: forceLogoutAndRedirect,
      token: authStore.authenticationState?.token ?? "",
      setApiError: (m) => setApiError(m ?? undefined),
      setLoading: setSubmitting,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate("/", { replace: true });
      },

    });

    if (!msg) {
      return;
    }

    onReplySuccess?.(contactId);
    onClose();
  };

  return (
    <form
      className={styles.inquirieForm}
      data-testid="reply-inquiry-form"
      onSubmit={handleSubmit(onValidSubmit)}
    >
      <div
        className={styles.inquirieFieldGroup}
        data-testid="reply-inquiry-original"
      >
        <label className={styles.mcdInputLabel}>Original inquiry</label>
        <div className={styles.inquirieReadonlyBox}>
          <div className={styles.inquirieReadonlyBody}>{originalMessage}</div>
        </div>
      </div>

      <div
        className={styles.inquirieFieldGroup}
        data-testid="reply-inquiry-title-container"
      >
        <label className={styles.mcdInputLabel}>Reply subject</label>
        <div className={styles.inquirieReadonlyBox}>
          <div
            className={styles.inquirieReadonlyBody}
            data-testid="reply-inquiry-title"
          >
            {replySubject}
          </div>
        </div>
      </div>

      <Controller
        name="message"
        control={control}
        rules={{
          validate: (value) => {
            const len = (value ?? "").trim().length;
            if (len === 0) {
              return "Reply message is required";
            }
            if (len < MIN_REPLY_LENGTH) {
              return `Reply message must be at least ${MIN_REPLY_LENGTH} characters`;
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => {
          const rawValue = field.value ?? "";
          const currentLength = rawValue.trim().length;
          const belowMin = currentLength < MIN_REPLY_LENGTH;

          return (
            <div
              className={styles.mcdFieldGroup}
              data-testid="reply-inquiry-message-group"
            >
              <label
                className={styles.mcdInputLabel}
                htmlFor="reply-inquiry-message"
              >
                Message
              </label>
              <textarea
                id="reply-inquiry-message"
                className={
                  styles.mcdTextarea +
                  " " +
                  (fieldState.error ? styles.mdcinputWithError : "")
                }
                value={rawValue}
                onChange={field.onChange}
                placeholder="Write your reply…"
                disabled={submitting}
                data-testid="reply-inquiry-message"
              />

              <div
                className={`${styles.charCounter} ${belowMin ? styles.charCounterWarn : ""
                  }`}
                data-testid="reply-inquiry-message-counter"
              >
                {currentLength} / {MIN_REPLY_LENGTH} characters minimum
              </div>

              {fieldState.error?.message && (
                <span
                  className={styles.mcdErrorText}
                  data-testid="reply-inquiry-message-error"
                >
                  {fieldState.error.message}
                </span>
              )}
            </div>
          );
        }}
      />

      <div className={styles.mcdActions} data-testid="reply-inquiry-actions">
        <button
          type="button"
          className={styles.mcdBtnGhost}
          onClick={onClose}
          disabled={submitting}
          data-testid="reply-inquiry-cancel"
        >
          Cancel
        </button>

        <Submission
          buttonText={submitting ? "Sending…" : "Send reply"}
          disabled={submitting}
          error={apiError}
          className={styles.mcdBtnPrimary}
          data-testid="reply-inquiry-submit"
        />
      </div>
    </form>
  );
};

export default ReplyToInquiryForm;
